import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function DeleteAccount() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", reason: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.includes("@")) return toast.error("Informe seu nome e o e-mail da conta");
    setSending(true);
    try {
      await api.post("/deletion-request", form);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar solicitação");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="delete-account-page">
      <span className="inline-flex items-center gap-2 bg-brand-coralLight text-brand-coral text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
        <Trash2 className="w-3.5 h-3.5" /> Direito do titular — LGPD
      </span>
      <h1 className="text-3xl sm:text-4xl font-bold font-heading text-brand-graphite">Excluir conta e dados</h1>
      <p className="text-sm text-muted-foreground mt-2">PetMachine Hub — petmachinehub.com.br · Você pode pedir a exclusão a qualquer momento, com ou sem login.</p>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-heading font-semibold text-brand-graphite mb-2">O que será excluído</h2>
          <ul className="list-disc pl-5 text-sm text-brand-graphite/80 space-y-1">
            <li>Sua conta (nome, e-mail, telefone, endereço)</li>
            <li>Pets cadastrados, fotos e carteiras de vacinação</li>
            <li>Tags/anilhas vinculadas e páginas públicas delas</li>
            <li>Avaliações, favoritos e notificações</li>
          </ul>
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-heading font-semibold text-brand-graphite mb-2">O que a lei nos obriga a manter</h2>
          <ul className="list-disc pl-5 text-sm text-brand-graphite/80 space-y-1">
            <li>Registros fiscais de vendas já concluídas (sem dados pessoais identificáveis)</li>
            <li>Logs de segurança pelo prazo legal mínimo</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-3">Prazo: até 15 dias (normalmente processamos antes). Confirmação por e-mail.</p>
        </div>
      </div>

      {done ? (
        <div className="bg-brand-mintLight rounded-2xl p-8 mt-8 text-center" data-testid="deletion-success">
          <ShieldCheck className="w-10 h-10 text-brand-mint mx-auto mb-3" />
          <p className="font-heading font-bold text-lg text-brand-graphite">Solicitação registrada</p>
          <p className="text-sm text-muted-foreground mt-1">Recebemos seu pedido e vamos processá-lo dentro do prazo legal. Você receberá a confirmação no e-mail informado.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border p-6 mt-8" data-testid="deletion-form">
          <h2 className="font-heading font-semibold text-lg text-brand-graphite mb-1">Solicitar exclusão</h2>
          <p className="text-xs text-muted-foreground mb-4">Use o mesmo e-mail da sua conta. Se preferir, também pode pedir pelo e-mail <strong>petmachineltda@gmail.com</strong>.</p>
          <div className="space-y-3">
            <div><Label>Seu nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="deletion-name-input" /></div>
            <div><Label>E-mail da conta *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@email.com" className="mt-1" data-testid="deletion-email-input" /></div>
            <div><Label>Motivo (opcional)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Ex.: não uso mais o serviço" className="mt-1" data-testid="deletion-reason-input" /></div>
            <Button onClick={submit} disabled={sending} variant="outline" className="w-full rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid="deletion-submit-btn">
              {sending ? "Enviando..." : "Solicitar exclusão da minha conta e dados"}
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-6 text-center">
        Veja também nossa <Link to="/privacidade" className="text-brand-coral font-semibold hover:underline" data-testid="deletion-privacy-link">Política de Privacidade</Link>
      </p>
    </div>
  );
}
